/**
 * The reason the server gave for refusing a request, if it gave one.
 *
 * `auth0BaseQuery` normalizes every failure to
 * `{ status, data: { status, data: <server body>, message, failureClass } }`.
 * The server's own sentence lives in that body — `ClientErrorResponse.message`,
 * or `detail` from a handler that still raises a bare HTTPException — and
 * only an HTTP response has one.
 *
 * Never the outer `message`: the base query fills it with generated text
 * when the server said nothing ("Request timed out", "Server error").
 * Shown as a refusal, that text displaced the dialogs' own "nothing was
 * changed" fallback on exactly the failures it exists for.
 *
 * One copy on purpose (gdn-4176): the Reverse and Remove dialogs each kept
 * their own, gdn-3641 fixed only Reverse's, and Remove went on reading a
 * field that is never filled — every refusal showed the generic fallback.
 * The balance-table dialogs read refusals through this one.
 *
 * Narrower than `extractApiErrorMessage` on purpose. That helper falls back
 * to transport errors and stringified JSON; a money dialog that gets no
 * sentence from the server has to say "nothing was changed" instead, so
 * this returns null and leaves the fallback to the caller.
 */
export const serverReasonOf = (error: unknown): string | null => {
  const failure = (error as { data?: { failureClass?: unknown; data?: unknown } } | undefined)
    ?.data;
  if (failure?.failureClass !== 'http') return null;
  const body = failure.data;
  if (typeof body !== 'object' || body === null) return null;
  const { message, detail } = body as { message?: unknown; detail?: unknown };
  if (typeof message === 'string' && message) return message;
  return typeof detail === 'string' && detail ? detail : null;
};
